import React, { useState, useRef } from "react";
import {
  IonPage,
  IonContent,
} from "@ionic/react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  IconButton,
  useMediaQuery,
  useTheme as useMuiTheme,
  styled,
  Link as MuiLink,
  createTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import {
  CloudUpload,
  Download,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Info,
  Dashboard,
  School,
  People,
  Settings,
  AdminPanelSettings,
  ArrowBack,
  Logout,
  Close as CloseIcon,
  FileUpload,
  Description,
  Group,
  HowToReg,
  Assessment,
} from "@mui/icons-material";
import { ThemeProvider } from "@emotion/react";
import axios from "axios";
import Papa from "papaparse";
import { baseUrl, logOutUser, getUser } from "../../common/utils";
import { useHistory, useLocation } from "react-router-dom";
import SchoolActivityStats from "./SchoolActivityStats";

// ============================================
// Modern Dashboard Theme (MUI5)
// ============================================
const theme = createTheme({
  palette: {
    primary: {
      main: "#243b5a",
    },
    secondary: {
      main: "#ef4444",
    },
    success: {
      main: "#059669",
    },
    error: {
      main: "#dc2626",
    },
    info: {
      main: "#3b82f6",
    },
    warning: {
      main: "#f59e0b",
    },
    background: {
      default: "#f4f6f8",
      paper: "#ffffff",
    },
    text: {
      primary: "#1f2937",
      secondary: "#6b7280",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontSize: "1.5rem",
      fontWeight: 700,
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 600,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 600,
    },
    body1: {
      fontSize: "0.875rem",
      color: "#6b7280",
    },
    body2: {
      fontSize: "0.875rem",
      color: "#374151",
    },
    caption: {
      fontSize: "0.75rem",
      letterSpacing: "1px",
      color: "#6b7280",
      textTransform: "uppercase",
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 600,
      color: "#ffffff",
    },
  },
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "none",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          borderRadius: "6px",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          padding: "6px 16px",
          borderRadius: 6,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: "#f8f9fa",
          fontSize: "13px",
          color: "#4b5563",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "#d1d5db",
          borderWidth: "1px",
        },
      },
    },
  },
});

// ============================================
// Styled Components
// ============================================
const Header = styled(Box)(({ theme }) => ({
  backgroundColor: "#243b5a",
  padding: "16px 40px",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  boxSizing: "border-box",
}));

const HeaderTopRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
});

const HeaderBottomRow = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  width: "100%",
  marginTop: "8px",
});

const LogoCircle = styled(Box)({
  width: "55px",
  height: "55px",
  borderRadius: "50%",
  backgroundColor: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px",
  color: "#243b5a",
  fontWeight: "bold",
  border: "2px solid #f0c45a",
  marginRight: "16px",
});

const FooterLink = styled(MuiLink)({
  color: "#d1d5db",
  textDecoration: "underline",
  cursor: "pointer",
  "&:hover": {
    color: "#ffffff",
  },
});

const SchoolInfoBox = styled(Box)({
  textAlign: "right",
  color: "#f1f5f9",
});

const LogoutButton = styled(Button)({
  backgroundColor: "#ef4444",
  color: "#ffffff",
  fontWeight: 600,
  fontSize: "14px",
  textTransform: "none",
  padding: "6px 16px",
  borderRadius: "6px",
  "&:hover": {
    backgroundColor: "#dc2626",
  },
  "& svg": {
    marginRight: "8px",
  },
});

const NavMenu = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginTop: "12px",
  flexWrap: "wrap",
});

const NavButton = styled(Button)<{ active?: boolean }>(({ active }) => ({
  color: active ? "#ffffff" : "#e0e7ff",
  backgroundColor: active ? "rgba(255,255,255,0.15)" : "transparent",
  fontWeight: active ? 600 : 500,
  fontSize: "14px",
  textTransform: "none",
  padding: "6px 16px",
  borderRadius: "20px",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.1)",
    color: "#ffffff",
  },
  "& svg": {
    marginRight: "8px",
    fontSize: "18px",
  },
}));

const UploadArea = styled(Box)({
  border: "2px dashed #cbd5e1",
  borderRadius: "12px",
  padding: "40px",
  textAlign: "center",
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: "#243b5a",
    backgroundColor: "#f8fafc",
  },
});

const AddResult: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<{
    inserted: number;
    updated: number;
    skipped: number;
    errors: string[];
  } | null>(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [instructionsModalOpen, setInstructionsModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [mappingStep, setMappingStep] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});

  const history = useHistory();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const user = getUser();
  const userData = {
    name: user?.name || "ADMIN USER",
    lgaCode: user?.lgaCode || "",
    schoolCode: user?.schoolCode || "",
  };

  const CSV_HEADERS = [
    "SESSIONYR",
    "PRCD",
    "REGISTRATIONNO",
    "FNAME",
    "MNAME",
    "LNAME",
    "LGACD",
    "SCHOOLCODE",
    "SEXCD",
    "EXAMINATIONNO",
    "ENG",
    "ENGGRD",
    "ARIT",
    "ARITGRD",
    "GP",
    "GPGRD",
    "RGS",
    "RGSGRD",
    "REMARK",
    "RGSTYPE"
  ];

  const SAMPLE_ROW = [
    "2024/2025",
    "1",
    "189",
    "nice",
    "tejiri",
    "omajurun",
    "17",
    "33",
    "M",
    "170330189",
    "82",
    "A",
    "78",
    "C",
    "80.875",
    "A",
    "74.625",
    "A",
    "Distinction",
    "1"
  ];

  const handleDownloadTemplate = () => {
    const csvContent = [
      CSV_HEADERS.join(','),
      SAMPLE_ROW.join(','),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'result_upload_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const processFile = (file: File) => {
    if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
      setSelectedFile(file);
      setUploadResult(null);
      setLoading(true);
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const headers = results.meta.fields || [];
          setCsvHeaders(headers);
          setParsedData(results.data);
          
          const initialMapping: Record<string, string> = {};
          CSV_HEADERS.forEach(sysHeader => {
            const sysLower = sysHeader.toLowerCase();
            if (headers.includes(sysHeader)) {
              initialMapping[sysHeader] = sysHeader;
            } else {
              const match = headers.find(h => {
                const hLower = h.toLowerCase().trim();
                if (hLower === sysLower) return true;
                if (sysLower === 'schoolcode' && (hLower === 'institutioncd' || hLower === 'school code')) return true;
                if (sysLower === 'fname' && (hLower === 'firstname' || hLower === 'first name')) return true;
                if (sysLower === 'lname' && (hLower === 'lastname' || hLower === 'last name')) return true;
                if (sysLower === 'rgstype' && hLower === 'rgstype') return true;
                return false;
              });
              if (match) initialMapping[sysHeader] = match;
            }
          });
          setColumnMapping(initialMapping);
          setMappingStep(true);
          setLoading(false);
        },
        error: (err) => {
          console.error(err);
          alert('Error parsing CSV file');
          setLoading(false);
        }
      });
    } else {
      if (fileInputRef.current) fileInputRef.current.value = '';
      alert('Please select a valid CSV file');
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleProcessAndUpload = async () => {
    if (!columnMapping['EXAMINATIONNO']) {
      alert("EXAMINATIONNO is required and must be mapped to a column.");
      return;
    }
    
    setLoading(true);
    setUploadProgress(0);

    try {
      const numericHeaders = ["ENG", "ARIT", "GP", "RGS"];
      const transformedData = parsedData.map(row => {
        const newRow: Record<string, string> = {};
        CSV_HEADERS.forEach(sysHeader => {
          const csvHeader = columnMapping[sysHeader];
          let val = (csvHeader && row[csvHeader] !== undefined) ? String(row[csvHeader]).trim() : "";
          if (numericHeaders.includes(sysHeader) && val === "") {
            val = "0";
          }
          newRow[sysHeader] = val;
        });
        return newRow;
      });

      const validData = transformedData.filter(row => row['EXAMINATIONNO'] && String(row['EXAMINATIONNO']).trim() !== '');

      if (validData.length === 0) {
        alert("No valid data found to upload. Please ensure EXAMINATIONNO is mapped correctly.");
        setLoading(false);
        return;
      }

      const newCsvStr = Papa.unparse(validData);
      const mappedFile = new File([newCsvStr], "mapped_results.csv", { type: "text/csv" });

      const formData = new FormData();
      formData.append('file', mappedFile);

      const res = await axios.post(`${baseUrl()}Admin2s/uploadResultsCsv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        },
      });

      // Handle both formats: wrapped {status: true, data: {...}} or direct {...} 
      const responseData = res.data.data ? res.data.data : res.data;
      const isSuccess = res.data.status === true || responseData.inserted !== undefined;

      if (isSuccess) {
        setUploadResult({
          inserted: responseData.inserted || 0,
          updated: responseData.updated || 0,
          skipped: responseData.skipped || 0,
          errors: responseData.errors || [],
        });
        setMappingStep(false);
        if (responseData.errors && responseData.errors.length > 0) {
          setErrorDialogOpen(true);
        }
      } else {
        alert(res.data.message || res.data.error || 'Upload failed');
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      alert(error.response?.data?.message || 'Error uploading file. Please check the format and try again.');
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const handleMenuClick = (menu: string) => {
    history.push(`/${menu}`);
  };

  const handleLogout = () => {
    logOutUser();
    history.push('/login_admin');
  };

  const handleReset = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setUploadProgress(0);
    setMappingStep(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <IonPage>
        <IonContent>
          <Box sx={{
            bgcolor: "background.default",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}>
            <Header>
              <HeaderTopRow>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LogoCircle>⚖️</LogoCircle>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#cbd5e1", letterSpacing: "0.5px", display: "block" }}>
                      DELTA STATE GOVERNMENT
                    </Typography>
                    <Typography variant="h6" sx={{ color: "#ffffff", fontWeight: 600, lineHeight: 1.2 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Add Result
                    </Typography>
                  </Box>
                </Box>

                <SchoolInfoBox>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, color: "#ffffff", textTransform: "uppercase" }}
                  >
                    {userData.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                    LGA: {userData.lgaCode} | Code: {userData.schoolCode}
                  </Typography>
                </SchoolInfoBox>
              </HeaderTopRow>

              <HeaderBottomRow>
                <LogoutButton
                  variant="contained"
                  onClick={handleLogout}
                  startIcon={<Logout />}
                >
                  Logout
                </LogoutButton>
              </HeaderBottomRow>

              <NavMenu>
                <NavButton
                  active={location.pathname === "/admin_dashboard"}
                  onClick={() => handleMenuClick("admin_dashboard")}
                  startIcon={<Dashboard />}
                >
                  Admin Dashboard
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_students"}
                  onClick={() => handleMenuClick("admin_students")}
                  startIcon={<People />}
                >
                  Students
                </NavButton>
                <NavButton
                  active={location.pathname === "/lga_student_database"}
                  onClick={() => handleMenuClick("lga_student_database")}
                  startIcon={<Group />}
                >
                  LGA Student Database
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_schools"}
                  onClick={() => handleMenuClick("admin_schools")}
                  startIcon={<School />}
                >
                  View Schools
                </NavButton>
                <NavButton
                  active={location.pathname === "/add_result"}
                  onClick={() => handleMenuClick("add_result")}
                  startIcon={<HowToReg />}
                >
                  Add Result
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_settings"}
                  onClick={() => handleMenuClick("admin_settings")}
                  startIcon={<Settings />}
                >
                  Settings
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_management"}
                  onClick={() => handleMenuClick("admin_management")}
                  startIcon={<AdminPanelSettings />}
                >
                  Management Console
                </NavButton>

                <NavButton
                  active={location.pathname === "/admin_stats"}
                  onClick={() => handleMenuClick("admin_stats")}
                  startIcon={<Assessment />}
                >
                  Activity Stats
                </NavButton>
              </NavMenu>
            </Header>


            <Container maxWidth="xl" sx={{ py: 4, flex: 1 }}>
              <Button
                onClick={() => history.goBack()}
                startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
                sx={{
                  color: "#243b5a",
                  fontSize: "13px",
                  textTransform: "none",
                  p: 0,
                  mb: 3,
                  '&:hover': {
                    textDecoration: "underline",
                    backgroundColor: "transparent",
                  },
                }}
              >
                Back
              </Button>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h4" fontWeight={800} color="primary">
                  Upload Examination Results
                </Typography>
                <Typography color="text.secondary">
                  Bulk upload student examination results via CSV file
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={7}>
                  <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Bulk Upload via CSV
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Upload a CSV file with student results. The file must include the following columns:
                    </Typography>

                    <Box sx={{ mb: 3, overflowX: 'auto' }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Required CSV Format:
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8fafc', overflowX: 'auto' }}>
                        <Typography variant="caption" component="pre" sx={{
                          fontFamily: 'monospace',
                          fontSize: '11px',
                          m: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}>
                          {CSV_HEADERS.join(', ')}
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" component="pre" sx={{
                          fontFamily: 'monospace',
                          fontSize: '11px',
                          m: 0,
                          color: 'text.secondary'
                        }}>
                          {SAMPLE_ROW.join(', ')}
                        </Typography>
                      </Paper>
                    </Box>

                    <Button
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={handleDownloadTemplate}
                      sx={{ mb: 3, mr: 2 }}
                    >
                      Download CSV Template
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<Description />}
                      onClick={() => setInstructionsModalOpen(true)}
                      sx={{ mb: 3 }}
                    >
                      View Instructions
                    </Button>

                    {mappingStep ? (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" color="primary" gutterBottom>
                          Map CSV Columns
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Please match your uploaded CSV columns to the required system fields. Unmapped fields will be sent as blank.
                        </Typography>
                        
                        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 400 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell>System Field</TableCell>
                                <TableCell>Your CSV Column</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {CSV_HEADERS.map(sysHeader => (
                                <TableRow key={sysHeader}>
                                  <TableCell sx={{ fontWeight: sysHeader === 'EXAMINATIONNO' ? 'bold' : 'normal' }}>
                                    {sysHeader} {sysHeader === 'EXAMINATIONNO' && <span style={{color: 'red'}}>*</span>}
                                  </TableCell>
                                  <TableCell>
                                    <FormControl fullWidth size="small">
                                      <Select
                                        value={columnMapping[sysHeader] || ""}
                                        onChange={(e) => setColumnMapping({...columnMapping, [sysHeader]: e.target.value})}
                                        displayEmpty
                                      >
                                        <MenuItem value=""><em>-- Not Mapped (Blank) --</em></MenuItem>
                                        {csvHeaders.map(h => (
                                          <MenuItem key={h} value={h}>{h}</MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Button
                            fullWidth
                            variant="contained"
                            startIcon={<CloudUpload />}
                            onClick={handleProcessAndUpload}
                            disabled={loading || !columnMapping['EXAMINATIONNO']}
                            sx={{ bgcolor: "#243b5a" }}
                          >
                            Confirm Mapping & Upload
                          </Button>
                          <Button variant="outlined" onClick={handleReset} disabled={loading}>
                            Cancel
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".csv"
                          id="csv-upload"
                          style={{ display: 'none' }}
                          onChange={handleFileSelect}
                        />
                        <label htmlFor="csv-upload">
                          <UploadArea
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                          >
                            <FileUpload sx={{ fontSize: 48, color: '#9ca3af', mb: 2 }} />
                            <Typography variant="body1" fontWeight="500">
                              {selectedFile ? selectedFile.name : 'Choose a CSV file or drag and drop'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Supported format: .csv
                            </Typography>
                          </UploadArea>
                        </label>
                      </>
                    )}

                    {loading && (
                      <Box sx={{ mt: 3 }}>
                        <LinearProgress variant="determinate" value={uploadProgress} />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Uploading... {uploadProgress}%
                        </Typography>
                      </Box>
                    )}
                  </Paper>

                  <Card>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        📋 Instructions
                      </Typography>
                      <Box component="ul" sx={{ pl: 2 }}>
                        <li>
                          <Typography variant="body2">
                            Ensure your CSV file follows the exact format shown above
                          </Typography>
                        </li>
                        <li>
                          <Typography variant="body2">
                            All examination numbers must be unique
                          </Typography>
                        </li>
                        <li>
                          <Typography variant="body2">
                            Required fields: <strong>SESSIONYR</strong>, <strong>EXAMINATIONNO</strong>
                          </Typography>
                        </li>
                        <li>
                          <Typography variant="body2">
                            The system will update existing records if examination number exists
                          </Typography>
                        </li>
                        <li>
                          <Typography variant="body2">
                            Any errors will be reported after upload
                          </Typography>
                        </li>
                        <li>
                          <Typography variant="body2" color="warning.main">
                            ⚠️ CSV headers are case-sensitive! Use exact column names as shown
                          </Typography>
                        </li>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={5}>
                  {uploadResult && (
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Upload Summary
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CheckCircle sx={{ color: '#059669' }} />
                              <Typography variant="body2">Inserted:</Typography>
                            </Box>
                            <Typography variant="h6" fontWeight="bold" color="success.main">
                              {uploadResult.inserted}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Info sx={{ color: '#3b82f6' }} />
                              <Typography variant="body2">Updated:</Typography>
                            </Box>
                            <Typography variant="h6" fontWeight="bold" color="info.main">
                              {uploadResult.updated}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Warning sx={{ color: '#f59e0b' }} />
                              <Typography variant="body2">Skipped:</Typography>
                            </Box>
                            <Typography variant="h6" fontWeight="bold" color="warning.main">
                              {uploadResult.skipped}
                            </Typography>
                          </Box>
                        </Grid>

                        {uploadResult.errors && uploadResult.errors.length > 0 && (
                          <Grid item xs={12}>
                            <Button
                              fullWidth
                              variant="outlined"
                              color="error"
                              startIcon={<ErrorIcon />}
                              onClick={() => setErrorDialogOpen(true)}
                              sx={{ mt: 2 }}
                            >
                              View Errors ({uploadResult.errors.length})
                            </Button>
                          </Grid>
                        )}
                      </Grid>

                      {(uploadResult.inserted > 0 || uploadResult.updated > 0) && (
                        <Alert severity="success" sx={{ mt: 2 }}>
                          Successfully processed {uploadResult.inserted + uploadResult.updated} records!
                        </Alert>
                      )}

                      {uploadResult.skipped > 0 && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          {uploadResult.skipped} record(s) were skipped due to errors.
                        </Alert>
                      )}
                    </Paper>
                  )}

                  <Card sx={{ mt: 3 }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        💡 Pro Tips
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2">
                          🔑 <strong>EXAMINATIONNO</strong> is the unique identifier
                        </Typography>
                        <Typography variant="body2">
                          📝 <strong>RGSTYPE</strong>: 1 = Christian (CRS), 2 = Muslim (IRS)
                        </Typography>
                        <Typography variant="body2">
                          🏫 <strong>INSTITUTIONCD</strong> and <strong>LGACD</strong> are required for linking
                        </Typography>
                        <Typography variant="body2">
                          📅 <strong>SESSIONYR</strong> format: YYYY/YYYY (e.g., 2025/2026)
                        </Typography>
                        <Typography variant="body2" color="error.main">
                          ⚠️ Headers are <strong>CASE-SENSITIVE</strong>! Use uppercase as shown
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Container>

            <Dialog
              open={errorDialogOpen}
              onClose={() => setErrorDialogOpen(false)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ErrorIcon color="error" />
                  <Typography variant="h6">Upload Errors</Typography>
                </Box>
                <IconButton onClick={() => setErrorDialogOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                {uploadResult?.errors && uploadResult.errors.length > 0 ? (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      The following errors occurred during upload:
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Error Message</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {uploadResult.errors.map((error, index) => (
                            <TableRow key={index}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>{error}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ) : (
                  <Typography variant="body2">No errors to display</Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setErrorDialogOpen(false)} variant="contained">
                  Close
                </Button>
              </DialogActions>
            </Dialog>

            {/* Instructions Modal */}
            <Dialog
              open={instructionsModalOpen}
              onClose={() => setInstructionsModalOpen(false)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Description sx={{ color: '#243b5a' }} />
                  <Typography variant="h6">Upload Instructions</Typography>
                </Box>
                <IconButton onClick={() => setInstructionsModalOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <Typography variant="body1" paragraph>
                  Follow these instructions to successfully upload examination results via CSV file:
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  📋 Required CSV Format
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8fafc', mb: 3 }}>
                  <Typography variant="caption" component="pre" sx={{
                    fontFamily: 'monospace',
                    fontSize: '11px',
                    m: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {CSV_HEADERS.join(', ')}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" component="pre" sx={{
                    fontFamily: 'monospace',
                    fontSize: '11px',
                    m: 0,
                    color: 'text.secondary'
                  }}>
                    {SAMPLE_ROW.join(', ')}
                  </Typography>
                </Paper>

                <Typography variant="h6" gutterBottom>
                  📝 Step-by-Step Instructions
                </Typography>
                <Box component="ol" sx={{ pl: 2 }}>
                  <li>
                    <Typography variant="body2" paragraph>
                      <strong>Download Template:</strong> Click "Download CSV Template" to get a sample file with the correct format.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" paragraph>
                      <strong>Prepare Your Data:</strong> Fill in your examination results following the exact column headers shown above. Headers are case-sensitive!
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" paragraph>
                      <strong>Required Fields:</strong> Ensure <strong>SESSIONYR</strong> (e.g., 2025/2026) and <strong>EXAMINATIONNO</strong> (unique identifier) are filled for each record.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" paragraph>
                      <strong>Unique Records:</strong> Each examination number must be unique. The system will update existing records if the examination number already exists.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" paragraph>
                      <strong>Upload File:</strong> Select your CSV file using the upload area. Only .csv files are accepted.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" paragraph>
                      <strong>Review Results:</strong> After upload, check the summary for inserted, updated, and skipped records. Any errors will be listed.
                    </Typography>
                  </li>
                </Box>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  ⚠️ Important Notes
                </Typography>
                <Box component="ul" sx={{ pl: 2 }}>
                  <li>
                    <Typography variant="body2">
                      CSV headers must match exactly (case-sensitive) as defined in the template above.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      RGSTYPE: Use 1 for Christian (CRS), 2 for Muslim (IRS)
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      SCHOOLCODE and LGACD are required for proper linking
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      SESSIONYR format: YYYY/YYYY (e.g., 2025/2026)
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="error.main">
                      Large files may take time to process. Please wait for the upload to complete.
                    </Typography>
                  </li>
                </Box>

                <Alert severity="info" sx={{ mt: 3 }}>
                  <Typography variant="body2">
                    <strong>Need Help?</strong> If you encounter issues, check your CSV format and ensure all required fields are present. Contact support if problems persist.
                  </Typography>
                </Alert>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setInstructionsModalOpen(false)} variant="contained">
                  Close
                </Button>
              </DialogActions>
            </Dialog>

            <Box component="footer" sx={{ bgcolor: "primary.main", color: "#d1d5db", py: { xs: 5, md: 6 }, mt: "auto" }}>
              <Container maxWidth="lg">
                <Grid container spacing={4} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      MINISTRY OF PRIMARY EDUCATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Delta State Government
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Asaba, Delta State, Nigeria
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      CONTACT INFORMATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Email: info@dsgmope.ng
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Address: Ministry Secretariat, Asaba
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Telephone: +234 (0)901 234 5678
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      OFFICIAL RESOURCES
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <FooterLink href="#">www.dsgmope.ng</FooterLink>
                    </Typography>
                    <Typography variant="body2">
                      <FooterLink href="#">Delta State Government Portal</FooterLink>
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", my: 3 }} />

                <Typography
                  variant="body2"
                  align="center"
                  sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem", maxWidth: 900, mx: "auto", mb: 2 }}
                >
                  OFFICIAL NOTICE: This is an official portal of the Delta State Government, Ministry of Primary Education. Unauthorized access, misuse, or tampering with this system is prohibited and may be subject to legal action. All information provided is for official purposes only.
                </Typography>

                <Typography variant="body2" align="center" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem" }}>
                  © 2026 Delta State Government, Ministry of Primary Education. All rights reserved.
                </Typography>
              </Container>
            </Box>
          </Box>
        </IonContent>
      </IonPage>
    </ThemeProvider>
  );
};

export default AddResult;
